import useLanguage from '@op/hooks/useLanguage';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Button, Checkbox, Justify, SearchBox } from 'tea-component';
import './index.scss'; 
import { useVirtualScroll } from '@op/hooks/useVirtualScroll';
import { NoDataCard } from '@op/components/NoDataCommon';
import _ from 'lodash-es';
import ChannelAccountItem from '../ChannelAccountItem'; 
import { useUpdateEffect } from 'ahooks';
import { AccountOption, getAccountListValue } from '../../../utils';


interface AccountListProps {
  options: AccountOption[];
  onChange?: (selectedAccounts: string[]) => void;
  defaultValue?: string[];
  value?: string[];
}


export default function AccountList({
  options: oriOptions,
  onChange,
  defaultValue,
  value,
}: AccountListProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(
    value || defaultValue || [],
  );

  useEffect(() => {
    if (value && !_.isEqual(value, selectedAccounts)) {
      setSelectedAccounts(value);
    }
  }, [value]);

  const { formatMessage } = useLanguage();

  // 使用 Set 优化查找性能
  const selectedAccountsSet = useMemo(
    () => new Set(selectedAccounts),
    [selectedAccounts],
  );

  // 当选中账号变化时，通知父组件
  useUpdateEffect(() => {
    onChange?.(selectedAccounts);
  }, [selectedAccounts]);

  // 缓存所有账号值，避免重复计算
  const allAccountValues = useMemo(() => {
    const values: string[] = [];
    oriOptions.forEach((option) => {
      option.children.forEach((child) => {
        values.push(child.value);
      });
    });
    return values;
  }, [oriOptions]);

  // 根据搜索文本过滤选项
  const { filteredOptions, total } = useMemo(() => {
    function filterListTotal(list: AccountOption[]) {
      const items = list.map((it) => it.children).flat(2);
      const total = items.length + list.length;
      return total;
    }

    if (!searchText) {
      return {
        filteredOptions: oriOptions,
        total: filterListTotal(oriOptions),
      };
    }

    const realSearchText = searchText.toLowerCase();

    const list = oriOptions
      .filter((option) => {
        // 检查一级是否命中
        const isParentMatched = option.text
          .toLowerCase()
          .includes(realSearchText);

        if (isParentMatched) {
          return true;
        }

        // 检查children是否有匹配项
        return option.children.some((child) =>
          child.account.toLowerCase().includes(realSearchText),
        );
      })
      .map((option) => {
        // 检查一级是否命中
        const isParentMatched = option.text
          .toLowerCase()
          .includes(realSearchText);

        // 如果一级未命中，则过滤children
        if (!isParentMatched) {
          const filteredChildren = option.children.filter((child) =>
            child.account.toLowerCase().includes(realSearchText),
          );

          return {
            ...option,
            children: filteredChildren,
          };
        }

        // 如果一级命中，返回原选项
        return option;
      });

    return {
      filteredOptions: list,
      total: filterListTotal(list),
    };
  }, [oriOptions, searchText]);

  // 获取过滤后的所有账号值
  const filterAllAccountValues = useMemo(() => {
    return getAccountListValue(filteredOptions);
  }, [filteredOptions]);

  // 检查是否全选
  const isAllSelected = useMemo(() => {
    if (allAccountValues.length === 0) return false;
    return allAccountValues.every((value) => selectedAccountsSet.has(value));
  }, [allAccountValues, selectedAccountsSet]);

  // 处理全选/取消全选
  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // 取消全选
      setSelectedAccounts([]);
    } else {
      // 全选
      setSelectedAccounts(filterAllAccountValues);
    }
  }, [isAllSelected, filterAllAccountValues]);

  // 处理单个账号的选择
  const handleAccountChange = useCallback((value: string, checked: boolean) => {
    setSelectedAccounts((prev) => {
      if (checked) {
        return [...prev, value];
      }
      return prev.filter((item) => item !== value);
    });
  }, []);

  // 处理整个渠道的选择
  const handleChannelChange = useCallback(
    (channelValue: string, checked: boolean) => {
      const channel = filteredOptions.find(
        (option) => option.value === channelValue,
      );
      if (!channel) return;

      const channelAccountValues = channel.children.map((child) => child.value);

      setSelectedAccounts((prev) => {
        if (checked) {
          // 选中该渠道下的所有账号
          const newSet = new Set([...prev, ...channelAccountValues]);
          return Array.from(newSet);
        }
        // 取消选中该渠道下的所有账号
        return prev.filter((value) => !channelAccountValues.includes(value));
      });
    },
    [filteredOptions],
  );

  // 检查渠道是否全选
  const isChannelSelected = useCallback(
    (channelValue: string) => {
      const channel = filteredOptions.find(
        (option) => option.value === channelValue,
      );
      if (!channel || channel.children.length === 0) return false;
      return channel.children.every((child) =>
        selectedAccountsSet.has(child.value),
      );
    },
    [filteredOptions, selectedAccountsSet],
  );

  // 检查渠道是否部分选中
  const isChannelIndeterminate = useCallback(
    (channelValue: string) => {
      const channel = filteredOptions.find(
        (option) => option.value === channelValue,
      );
      if (!channel || channel.children.length === 0) return false;
      const selectedCount = channel.children.filter((child) =>
        selectedAccountsSet.has(child.value),
      ).length;
      return selectedCount > 0 && selectedCount < channel.children.length;
    },
    [filteredOptions, selectedAccountsSet],
  );

  // 使用自定义 Hook 实现虚拟滚动
  const { visibleItems, paddingTop, paddingBottom, containerProps } =
    useVirtualScroll({
      itemHeight: 36, // 每个选项的高度
      visibleCount: 8, // 可见区域显示的选项数量
      totalCount: total, // 总选项数量
    });

  return (
    <div className="dbo-account-list-wrapper">
      <SearchBox
        onChange={value => setSearchText(value)}
        onSearch={(value = '') => setSearchText(value)}
        onClear={() => setSearchText('')} 
        placeholder={formatMessage("Search")}
      />
      <Justify
        className="dbo-header"
        left={<div className="dbo-left-title">{formatMessage("Account")}</div>}
        right={
          <Button
            type="link"
            className="dbo-right-btn"
            onClick={handleSelectAll}
          >
            {isAllSelected
              ? formatMessage('Clear All')
              : formatMessage('Select All')}
          </Button>
        }
      />
      <NoDataCard
        empty={filteredOptions.length === 0}
        hasImg={false}
        hasDesc={false}
        height={80}
      >
        <div {...containerProps} className="dbo-check-list-box">
          <div
            style={{
              paddingTop: `${paddingTop}px`,
              paddingBottom: `${paddingBottom}px`,
            }}
          >
            <ChannelCheckboxMemo
              isChannelIndeterminate={isChannelIndeterminate}
              isChannelSelected={isChannelSelected}
              handleChannelChange={handleChannelChange}
              filteredOptions={filteredOptions}
              selectedAccountsSet={selectedAccountsSet}
              handleAccountChange={handleAccountChange}
              visibleItems={visibleItems}
            />
          </div>
        </div>
      </NoDataCard>
    </div>
  );
}

type RenderItemOption = {
  text: string;
  value: string;
  isOneLevel: boolean;
  channel: any;
};

function ChannelCheckbox({
  isChannelIndeterminate,
  isChannelSelected,
  handleChannelChange,
  selectedAccountsSet,
  handleAccountChange,
  filteredOptions,
  visibleItems,
}: {
  isChannelSelected: any;
  isChannelIndeterminate: any;
  handleChannelChange: any;
  handleAccountChange: any;
  selectedAccountsSet: any;
  filteredOptions: AccountOption[];
  visibleItems: any;
}) {
  const options = useMemo(() => {
    const list: Array<RenderItemOption> = [];
    filteredOptions.forEach((item) => {
      list.push({
        text: item.text,
        value: item.value,
        channel: item.value,
        isOneLevel: true,
      });

      item.children.forEach((child) => {
        list.push({
          channel: child.channel,
          text: child.account,
          value: child.value,
          isOneLevel: false,
        });
      });
    });

    return list;
  }, [filteredOptions]);

  return (
    <div className="dbo-channel-check-box-wrapper">
      {visibleItems.map((key: number) => {
        const item: RenderItemOption = options[key];
        if (item.isOneLevel) {
          const channelSelected = isChannelSelected(item.value);
          const channelIndeterminate = isChannelIndeterminate(item.value);

          return (
            <Checkbox
              className="dbo-channel-check-item"
              key={item.value}
              value={channelSelected}
              indeterminate={channelIndeterminate}
              onChange={(checked) => handleChannelChange(item.value, checked)}
            >
              <ChannelAccountItem item={item}></ChannelAccountItem>
            </Checkbox>
          );
        }

        return (
          <Checkbox
            className="dbo-channel-check-item dbo-second-level"
            key={item.value}
            value={selectedAccountsSet.has(item.value)}
            onChange={(checked) => handleAccountChange(item.value, checked)}
          >
            <ChannelAccountItem item={item}></ChannelAccountItem>
          </Checkbox>
        );
      })}
    </div>
  );
}

// 使用 React.memo 优化子组件渲染
const ChannelCheckboxMemo = React.memo(ChannelCheckbox);
